import { UAParser } from 'ua-parser-js';

export interface DeviceInfo {
  device: string;
  location: string;
  ip: string;
  time: string;
  fingerprint: string;
}

export function getDeviceFingerprint(): string {
  const parser = new UAParser();
  const result = parser.getResult();

  // Create a unique fingerprint from browser, OS, and device info
  const fingerprint = `${result.browser.name || 'unknown'}-${result.browser.version || 'unknown'}-${result.os.name || 'unknown'}-${result.os.version || 'unknown'}-${result.device.type || 'desktop'}`;

  return fingerprint;
}

export async function getDeviceInfo(): Promise<Omit<DeviceInfo, 'fingerprint'>> {
  const parser = new UAParser();
  const result = parser.getResult();

  // Get device type and browser
  const deviceType = result.device.type || 'Desktop';
  const browser = result.browser.name || 'Unknown Browser';
  const os = result.os.name || 'Unknown OS';

  const device = `${deviceType} - ${browser} on ${os}`;

  // Try to get location and IP (using a public IP API)
  let location = 'Unknown Location';
  let ip = 'Unknown IP';

  try {
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      location = `${data.city || 'Unknown'}, ${data.country_name || 'Unknown'}`;
      ip = data.ip || 'Unknown IP';
    }
  } catch (error) {
    // Failed to get location info
  }

  const time = new Date().toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  return { device, location, ip, time };
}

export function getStoredDevices(userId: string): string[] {
  if (typeof window === 'undefined') return [];

  const stored = localStorage.getItem(`devices_${userId}`);
  return stored ? JSON.parse(stored) : [];
}

export function isNewDevice(userId: string, fingerprint: string): boolean {
  const devices = getStoredDevices(userId);
  return !devices.includes(fingerprint);
}

export function storeDevice(userId: string, fingerprint: string): void {
  if (typeof window === 'undefined') return;

  const devices = getStoredDevices(userId);
  if (!devices.includes(fingerprint)) {
    devices.push(fingerprint);
    localStorage.setItem(`devices_${userId}`, JSON.stringify(devices));
  }
}
